(ns portal.third-party-credentials.meta-table
  (:require ["@mui/material/Box$default" :as Box]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Link$default" :as Link]
            ["@mui/material/List$default" :as List]
            ["@mui/material/ListItem$default" :as ListItem]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/SvgIcon$default" :as SvgIcon]
            ["@mui/material/Tooltip$default" :as Tooltip]
            ["@mui/material/Typography$default" :as Typography]
            ["@mui/x-data-grid-premium" :refer [DataGridPremium GridActionsCellItem]]
            ["src/svg/editor/objects/link.svg$default" :as LinkIcon]
            ["src/svg/files_folders/bin.svg$default" :as Bin]
            ["src/svg/inform/error-triangle-fill.svg$default" :as AlertIcon]
            ["src/svg/organisations/other/facebook.svg$default" :as Facebook]
            ["src/svg/organisations/other/instagram.svg$default" :as Instagram]
            ["src/svg/organisations/other/meta.svg$default" :as Meta]
            ["src/svg/wpp/content-actions/edit.svg$default" :as Edit]
            [clojure.string :as str]
            [common.modal.view :refer [defmodal modal-v2]]
            [common.queries :refer [use-query use-mutation]]
            [common.resource.query.adset :refer [adset-query]]
            [common.resource.query.social-settings :as social-settings.q]
            [common.widgets.data-grid :as dg]
            [re-frame.core :as rf]
            [reitit.frontend.easy :as r.easy]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defui adset-link [{:keys [adset-id]}]
  (let [{:keys [agency-id]} (use-subscribe [:url-context])
        {{campaign-id :campaignId
          advertiser-id :advertiserId
          adset-name :name} :data} (use-query (adset-query {:adset-id adset-id}))]
    ($ Stack {:direction "row"
              :alignItems "center"
              :spacing 1
              :sx #js {:pt 2}}
       ($ SvgIcon {:component LinkIcon})
       ($ Link {:target "_blank"
                :href (r.easy/href :adset/ad-variants {:agency-id agency-id
                                                       :advertiser-id advertiser-id
                                                       :campaign-id campaign-id
                                                       :adset-id adset-id})}
          adset-name))))

(defmodal ids-used-modal :social-credentials/ids-used-modal
  [{:keys [usages is-change]}]
  ($ modal-v2 {:title (str "Unable to " (if is-change "update" "delete")
                           " Ad account configuration")
               :icon ($ SvgIcon {:color "warning"} ($ AlertIcon))
               :footer ($ Button {:variant "contained"
                                  :sx #js {:color "text.primary"
                                           :bgcolor "warning.main"}
                                  :onClick #(rf/dispatch [:modal/close])}
                          "Understood")}
     ($ Stack {:sx #js {:maxWidth "55rem"}}
        ($ Typography
           "These social credentials are currently being used in " (count usages)
           " adset(s), so they can't be deleted. To remove them, first update the adsets below with different credentials:")
        ($ List
           (for [{:keys [adset-id]} usages]
             ($ ListItem {:key adset-id :disablePadding true}
                ($ adset-link {:adset-id adset-id})))))))

(def string-array-comparator (fn [v1 v2] (compare (apply str v1) (apply str v2))))

(defn render-id-list [icon ^js params]
  (let [[first-id & rest-ids] (.-value params)]
    ($ Stack {:direction "row"
              :alignItems "center"
              :gap 1}
       ($ SvgIcon {:component icon})
       first-id
       (when (seq rest-ids)
         ($ Tooltip {:title (str/join ", " rest-ids)}
            ($ Box "+" (count rest-ids)))))))

(defui edit-action [{:keys [row]}]
  ($ GridActionsCellItem
     {:icon ($ SvgIcon {:inheritViewBox true :component Edit})
      :label "Edit"
      :onClick #(rf/dispatch [:ocp/open {:id :social/config
                                         :props (assoc row :type :meta)}])}))

(defui delete-action [{:keys [id]}]
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        {delete-settings :mutate}
        (use-mutation (social-settings.q/delete-social-settings-mutation
                       advertiser-id
                       #(rf/dispatch
                         [:modal/open {:id :social-credentials/ids-used-modal
                                       :props {:usages (:usages %)}}])))]
    ($ GridActionsCellItem
       {:icon ($ SvgIcon {:component Bin})
        :label "Delete"
        :onClick #(delete-settings {:meta-ad-account-id id})})))

(def ^:private columns
  #js [#js {:field "name"
            :headerName "Name"
            :type "string"
            :minWidth 120
            :flex 1
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:description row))}
       #js {:field "ad-account"
            :headerName "Ad account"
            :type "string"
            :minWidth 140
            :flex 1
            :valueGetter (fn [_value row] [(:id row)])
            :sortComparator string-array-comparator
            :renderCell (partial render-id-list Meta)}
       #js {:field "facebook-page-id"
            :headerName "Facebook page ID"
            :type "string"
            :minWidth 160
            :flex 1
            :valueGetter (fn [_value row] (:meta-page-ids row))
            :sortComparator string-array-comparator
            :renderCell (partial render-id-list Facebook)}
       #js {:field "instagram-id"
            :headerName "Instagram ID"
            :type "string"
            :minWidth 160
            :flex 1
            :valueGetter (fn [_value row] (:meta-instagram-user-ids row))
            :sortComparator string-array-comparator
            :renderCell (partial render-id-list Instagram)}
       #js {:field "actions"
            :type "actions"
            :getActions (fn [^js params]
                          (let [row (.-row params)]
                            #js [($ edit-action {:row row})
                                 ($ delete-action {:id (:id row)})]))}])

(defui social-credentials-table []
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        {load-success :isSuccess
         social-accounts :data} (use-query (social-settings.q/social-settings-query
                                            advertiser-id))]
    ($ DataGridPremium
       {:columns columns
        :loading (not load-success)
        :rows (to-array social-accounts)
        :getRowId (fn [row] (:id row))
        :autoHeight true
        :disableRowSelectionOnClick true
        :localeText #js {:noRowsLabel "No Meta credentials configured"}
        :sx (clj->js dg/actions-column-sx)})))
